import { getAccessToken } from './auth';
import { getConfig } from './config';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

type Auth = 'director' | 'station' | 'none';

const STATION_KEY = 'splitline.station';

export interface StationSession {
  token: string;
  stationId: string;
  raceId: string;
}

export function getStationSession(): StationSession | null {
  try {
    const raw = localStorage.getItem(STATION_KEY);
    return raw ? (JSON.parse(raw) as StationSession) : null;
  } catch {
    return null;
  }
}

export function setStationSession(s: StationSession | null) {
  if (s) localStorage.setItem(STATION_KEY, JSON.stringify(s));
  else localStorage.removeItem(STATION_KEY);
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown; auth?: Auth } = {}): Promise<T> {
  const { apiUrl, authMode } = getConfig();
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  const auth = opts.auth ?? 'director';
  if (auth === 'director') {
    if (authMode === 'dev') {
      headers['x-dev-user'] = localStorage.getItem('splitline.devUser') ?? 'dev-user';
      if (localStorage.getItem('splitline.devAdmin') === 'true') headers['x-dev-admin'] = 'true';
    }
    else {
      const token = await getAccessToken();
      if (token) headers.authorization = `Bearer ${token}`;
    }
  } else if (auth === 'station') {
    const s = getStationSession();
    if (s) headers.authorization = `Bearer ${s.token}`;
  }
  const res = await fetch(`${apiUrl}/api${path}`, {
    method: opts.method ?? (opts.body === undefined ? 'GET' : 'POST'),
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });
  if (res.status === 204) return undefined as T;
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const message = (json as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
    if (res.status === 401 && auth === 'director' && location.pathname !== '/login') location.assign('/login');
    throw new ApiError(res.status, message, json);
  }
  return json as T;
}
